import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Grid, Stack, Tooltip, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Flex, HStack } from "@zfp-akademie-a-s/zfp-design";
import { FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { InfoIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { type InvestmentsFormType, getInvestmentsFormSchema } from "Forms/Schemas/GoalsFulfillment/ProposedSolution/investmentsFormSchema";
import {
  getGetAssetAllocationOverviewQueryKey,
  getListContractProposalsQueryKey,
  useCreateContractProposal,
  usePatchContractProposal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  ContractProposalBaseInvestmentToolType,
  CreateContractProposalInvestmentBodyInvestmentToolType,
  PatchContractProposalInvestmentBodyInvestmentToolType,
} from "Infrastructure/ApiZfp/Schema";
import type {
  ContractProposalBaseSolutionHorizonType,
  CreateContractProposalInvestmentBody,
  PatchContractProposalInvestmentBody,
} from "Infrastructure/ApiZfp/Schema";
import type { ContractProposalDetailInvestment } from "Infrastructure/ApiZfp/Schema/contractProposalDetailInvestment";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useGoalsFulfillmentContext } from "../../GoalsFulfillmentContext";
import { CharacteristicsAccordion } from "../CharacteristicsAccordion";

type Props = {
  readonly edit?: boolean;
  readonly partner?: string;
  readonly proposedSolutionId?: string;
  readonly horizonType: ContractProposalBaseSolutionHorizonType;
  readonly onSuccess?: () => void;
  readonly initialData?: ContractProposalDetailInvestment | null;
};

export const InvestmentsForm = forwardRef<{}, Props>(({ partner, edit, proposedSolutionId, horizonType, onSuccess, initialData }, ref) => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  const partnerRef = useRef(partner);
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { assetAllocationOverview } = useGoalsFulfillmentContext();

  const { mutate: createMutation } = useCreateContractProposal();
  const { mutate: updateMutation } = usePatchContractProposal();

  const methods = useForm<InvestmentsFormType>({
    resolver: yupResolver(getInvestmentsFormSchema(t, assetAllocationOverview?.isPortfolioAssetAllocationRequired)),
    mode: "onChange",
    defaultValues:
      edit && initialData
        ? {
            investmentToolType: initialData.investmentToolType,
            partner: partner ?? "",
            oneTimeDeposit: initialData.oneTimeAmount?.value ?? 0,
            regularAmount: initialData.regularAmount?.value ?? 0,
            horizon: initialData.currentSolutionHorizon ?? 0,
            appreciationRate: initialData.appreciationRate ?? 0,
            entryFee: initialData.entryFee ?? 0,
            oneTimeFee: initialData.oneTimeFee?.value ?? 0,
            managementFee: initialData.managementFee ?? 0,
            exitFee: initialData.exitFee ?? 0,
            equities: initialData.assetAllocation?.equities ?? 0,
            bonds: initialData.assetAllocation?.bonds ?? 0,
            commodities: initialData.assetAllocation?.commodities ?? 0,
            realEstate: initialData.assetAllocation?.realEstate ?? 0,
            cash: initialData.assetAllocation?.cash ?? 0,
            other: initialData.assetAllocation?.others ?? 0,
            note: initialData.note ?? "",
          }
        : {
            investmentToolType: ContractProposalBaseInvestmentToolType.investment,
            partner: partner ?? "",
          },
  });

  const { control, handleSubmit, reset, trigger, setValue } = methods;

  useEffect(() => {
    if (edit && initialData) {
      reset({
        investmentToolType: initialData.investmentToolType,
        partner: partnerRef.current ?? "",
        oneTimeDeposit: initialData.oneTimeAmount?.value ?? 0,
        regularAmount: initialData.regularAmount?.value ?? 0,
        horizon: initialData.currentSolutionHorizon ?? 0,
        appreciationRate: initialData.appreciationRate ?? 0,
        entryFee: initialData.entryFee ?? 0,
        oneTimeFee: initialData.oneTimeFee?.value ?? 0,
        managementFee: initialData.managementFee ?? 0,
        exitFee: initialData.exitFee ?? 0,
        equities: initialData.assetAllocation?.equities ?? 0,
        bonds: initialData.assetAllocation?.bonds ?? 0,
        commodities: initialData.assetAllocation?.commodities ?? 0,
        realEstate: initialData.assetAllocation?.realEstate ?? 0,
        cash: initialData.assetAllocation?.cash ?? 0,
        other: initialData.assetAllocation?.others ?? 0,
        note: initialData.note ?? "",
      });
    }
  }, [edit, initialData, reset]);

  useEffect(() => {
    setValue("partner", partner ?? "", { shouldValidate: true });
  }, [partner, setValue]);

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (edit) {
        handleSubmit(onEdit)();
      } else {
        handleSubmit(onCreate)();
      }
    },
    triggerValidation: () => {
      trigger();
    },
  }));

  const onCreate = (data: InvestmentsFormType) => {
    if (!horizonType) return;

    const parsedPartnerId = Number(data.partner);
    const payload: CreateContractProposalInvestmentBody = {
      investmentToolType: CreateContractProposalInvestmentBodyInvestmentToolType.investment,
      solutionHorizonType: horizonType,
      currentSolutionHorizon: data.horizon ?? 0,
      appreciationRate: data.appreciationRate,
      managementFee: data.managementFee ?? 0,
      entryFee: data.entryFee ?? 0,
      exitFee: data.exitFee ?? 0,
      note: data.note ?? "",
      partnerId: Number.isFinite(parsedPartnerId) ? parsedPartnerId : 0,
      oneTimeAmount: { value: data.oneTimeDeposit ?? 0, currencyCode: "CZK", frequency: AmountFrequency.one_time },
      regularAmount: { value: data.regularAmount ?? 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
      oneTimeFee: { value: data.oneTimeFee ?? 0, currencyCode: "CZK", frequency: AmountFrequency.one_time },
      assetAllocation: {
        equities: data.equities ?? 0,
        bonds: data.bonds ?? 0,
        commodities: data.commodities ?? 0,
        realEstate: data.realEstate ?? 0,
        cash: data.cash ?? 0,
        others: data.other ?? 0,
      },
    };

    createMutation(
      { analysisId, versionId, data: payload },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListContractProposalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
          onSuccess?.();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
        },
      },
    );
  };

  const onEdit = (data: InvestmentsFormType) => {
    if (!proposedSolutionId) return;

    const parsedPartnerId = Number(data.partner);
    const payload: PatchContractProposalInvestmentBody = {
      investmentToolType: PatchContractProposalInvestmentBodyInvestmentToolType.investment,
      solutionHorizonType: horizonType,
      currentSolutionHorizon: data.horizon ?? 0,
      appreciationRate: data.appreciationRate,
      managementFee: data.managementFee ?? 0,
      entryFee: data.entryFee ?? 0,
      exitFee: data.exitFee ?? 0,
      note: data.note ?? "",
      partnerId: Number.isFinite(parsedPartnerId) ? parsedPartnerId : 0,
      oneTimeAmount: { value: data.oneTimeDeposit ?? 0, currencyCode: "CZK", frequency: AmountFrequency.one_time },
      regularAmount: { value: data.regularAmount ?? 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
      oneTimeFee: { value: data.oneTimeFee ?? 0, currencyCode: "CZK", frequency: AmountFrequency.one_time },
      assetAllocation: {
        equities: data.equities ?? 0,
        bonds: data.bonds ?? 0,
        commodities: data.commodities ?? 0,
        realEstate: data.realEstate ?? 0,
        cash: data.cash ?? 0,
        others: data.other ?? 0,
      },
    };

    updateMutation(
      { analysisId, versionId, contractProposalGuid: proposedSolutionId, data: payload },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListContractProposalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
          onSuccess?.();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <Box>
            <Grid container spacing={Spacing.XL}>
              {initialData?.productName && (
                <Grid size={12}>
                  <HStack gap={1} alignItems={"center"}>
                    <Typography fontWeight={500} variant="body2">
                      {t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Product)}:
                    </Typography>
                    <Typography variant="body2">{initialData.productName}</Typography>
                    <Tooltip title={t(Resources.AnalysisDetail.GoalsFulfillment.ProductNotAllowChange)}>
                      <Flex alignItems="center" sx={{ cursor: "pointer", color: "grey.400", "&:hover": { color: "grey.800" } }}>
                        <InfoIcon sx={{ fontSize: 16 }} />
                      </Flex>
                    </Tooltip>
                  </HStack>
                </Grid>
              )}
              <Grid size={6}>
                <FormNumberField
                  control={control}
                  name="oneTimeDeposit"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.OneTimeDeposit)}
                  required
                  scale={2}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <FormNumberField
                  control={control}
                  name="regularAmount"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.RegularAmount)}
                  required
                  scale={2}
                  thousandsSeparator=" "
                  unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                  fullWidth
                />
              </Grid>

              <Grid size={6}>
                <FormNumberField
                  control={control}
                  name="horizon"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Horizon)}
                  required
                  scale={0}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.Years)}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <FormNumberField
                  control={control}
                  name="appreciationRate"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.AppreciationRate)}
                  required
                  scale={2}
                  thousandsSeparator=" "
                  unit="%"
                  fullWidth
                />
              </Grid>

              <Stack direction="column">
                <Box sx={{ width: "100%" }}>
                  <Typography variant="body2" color="secondary" fontWeight={500}>
                    {t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Fees)}
                  </Typography>
                </Box>

                <Grid container spacing={Spacing.XL}>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="entryFee"
                      label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.EntryFeeShort)}
                      scale={2}
                      thousandsSeparator=" "
                      unit="%"
                      fullWidth
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="oneTimeFee"
                      label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.OneTimeFeeShort)}
                      scale={2}
                      thousandsSeparator=" "
                      unit={t(Resources.Common.CZKSuffix)}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="managementFee"
                      label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ManagementFeeShort)}
                      scale={2}
                      thousandsSeparator=" "
                      unit="%"
                      fullWidth
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="exitFee"
                      label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ExitFeeShort)}
                      scale={2}
                      thousandsSeparator=" "
                      unit="%"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Grid>
          </Box>

          <Box>
            <CharacteristicsAccordion<InvestmentsFormType>
              key={assetAllocationOverview?.isPortfolioAssetAllocationRequired ? "open" : "closed"}
              defaultExpanded={assetAllocationOverview?.isPortfolioAssetAllocationRequired}
            />
          </Box>

          <Divider />

          <Box>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="note"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Note)}
                  maxLength={NOTE_MAX_LENGTH}
                  placeholder={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.NotePlaceholder)}
                  multiline
                  minRows={6}
                  maxRows={8}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
});
